import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    
    if (!error.response) {
      console.error('Network error:', error);
      return Promise.reject(new Error('Network error. Please check your internet connection.'));
    }
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data: any) => api.post('/auth/register', data),
  login: (data: any) => api.post('/auth/login', data),
  verifyEmail: (data: any) => api.post('/auth/verify-email', data),
  resendVerification: (email: string) => api.post('/auth/resend-verification', { email }),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data: any) => api.post('/auth/reset-password', data),
  logout: () => api.post('/auth/logout'),
  changePassword: (data: any) => api.post('/auth/change-password', data),
};

export const profileAPI = {
  getProfile: () => api.get('/profile'),
  getBlockInfo: () => api.get('/profile/block-info'),
  getAdminProfile: () => api.get('/profile/admin'),
};

export const adminAPI = {
  getAllUsers: () => api.get('/admin/users'),
  getUserById: (id: string) => api.get(`/admin/users/${id}`),
  blockUser: (id: string, data: any) => api.patch(`/admin/users/${id}/block`, data),
  unblockUser: (id: string) => api.patch(`/admin/users/${id}/unblock`),
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
  getAllSupportMessages: () => api.get('/admin/support-messages'),
  markSupportMessageAsRead: (id: string) => api.patch(`/admin/support-messages/${id}/read`),
  sendSupportMessage: (data: any) => api.post('/admin/support-message', data),
  getStatistics: () => api.get('/admin/statistics'),
  getTopicsPreview: () => api.get('/topics?limit=5'),
};

export const topicAPI = {
  getAllTopics: (status?: string) => {
    const params = status ? `?status=${status}` : '';
    return api.get(`/topics${params}`);
  },
  getPublicTopics: () => api.get('/topics/public'),
  getPlatformStatistics: () => api.get('/topics/platform-statistics'),
  getTopicById: (id: string) => api.get(`/topics/${id}`),
  getFavoriteTopics: () => api.get('/topics/favorites'),
  isTopicFavorite: (id: string) => api.get(`/topics/${id}/favorite`),
  addTopicToFavorites: (id: string) => api.post(`/topics/${id}/favorite`),
  removeTopicFromFavorites: (id: string) => api.delete(`/topics/${id}/favorite`),
  createTopic: (data: any) => api.post('/topics', data),
  suggestTopic: (data: any) => api.post('/topics/suggest', data),
  updateTopic: (id: string, data: any) => api.patch(`/topics/admin/${id}`, data),
  deleteTopic: (id: string) => api.delete(`/topics/${id}`),
  approveTopic: (id: string) => api.patch(`/topics/${id}/approve`),
  rejectTopic: (id: string) => api.patch(`/topics/${id}/reject`),
  getPendingTopics: () => api.get('/topics/admin/pending'),
};

export const ideaAPI = {
  getIdeasByTopic: (topicId: string) => api.get(`/ideas?topicId=${topicId}`),
  createIdea: (data: any) => api.post('/ideas', data),
  updateIdea: (ideaId: string, data: any) => api.patch(`/ideas/${ideaId}`, data),
  likeIdea: (ideaId: string) => api.post(`/ideas/${ideaId}/like`),
  dislikeIdea: (ideaId: string) => api.post(`/ideas/${ideaId}/dislike`),
  pinIdea: (ideaId: string) => api.post(`/ideas/${ideaId}/pin`),
  unpinIdea: (ideaId: string) => api.post(`/ideas/${ideaId}/unpin`),
  getIdeaById: (ideaId: string) => api.get(`/ideas/${ideaId}`),
  getComments: (ideaId: string) => api.get(`/ideas/${ideaId}/comments`),
  addComment: (ideaId: string, data: { content: string; parentId?: string }) => api.post(`/ideas/${ideaId}/comments`, data),
  deleteComment: (commentId: string) => api.delete(`/ideas/comments/${commentId}`),
  getUserReaction: (ideaId: string) => api.get(`/ideas/${ideaId}/my-reaction`),
  getUserStatistics: () => api.get('/ideas/profile/statistics'),
  getAllIdeas: () => api.get('/ideas/admin/all'),
  deleteIdea: (ideaId: string) => api.delete(`/ideas/admin/${ideaId}`),
  getAllComments: () => api.get('/ideas/admin/comments/all'),
  deleteCommentAsAdmin: (commentId: string) => api.delete(`/ideas/admin/comments/${commentId}`),
};

export default api;